import { Role } from 'auth';
import dayjs from 'dayjs';
import { pickBy } from 'lodash';
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { Navigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import { ArchiveSwitch } from '@/modules/common/components/ArchiveSwitch.tsx';
import { DateVariant, dateToPrint } from '@/modules/common/utils/date-format.ts';
import { formatNumberWithCommas } from '@/modules/common/utils/format-numbers.ts';
import { IssuerDashboardWrapperOutletContext } from '@/modules/issuer-dashboard/components/IssuerDashboardWrapper.tsx';
import { transactionsFilters } from '@/modules/issuer-dashboard/components/holder-tabs/TransactionsTab.tsx';
import { IssuerAddBuyBackModal } from '@/modules/issuer-dashboard/components/modals/IssuerAddBuyBackModal.tsx';
import { IssuerBuyBackOperationInfoModal } from '@/modules/issuer-dashboard/components/modals/IssuerBuyBackOperationInfoModal.tsx';
import { useGetRegister } from '@/modules/issuer-dashboard/hooks/useGetRegister.tsx';
import { FiltersPanel } from '@/modules/issuer-dashboard/pages/IssuerTransactions.tsx';
import {
  useBuyBackEventArchiveByIdMutation,
  useBuyBackEventsQuery,
} from '@/modules/issuer-dashboard/queries/buyBack.tsx';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import {
  BodyCell,
  Button,
  ConfirmModal,
  ModalProviderContext,
  PageLayout,
  Progress,
  Spinner,
  Table,
  TableBodyColumn,
  TableHeadColumn,
  TooltipNew,
} from 'ui';
import { useIsMobile, useSaveState } from 'ui/hooks';
import { TrashGrayIcon } from 'ui/icons';
import {
  BuyBackEventCalculateResponse,
  GetBuyBackEventListV1SrBuyBackEventsGetParams,
  RegisterType,
} from 'api';

type TypeHead = 'ID' | 'Class' | 'Accepted offers' | 'Term' | 'Holders' | 'Total' | '';

const IssuerBuyBack = () => {
  const [params] = useSearchParams();
  const register = useGetRegister();
  const [isDeleted, setIsDeleted] = useState<boolean | undefined>();

  if (!!register?.type && register?.type !== RegisterType.Company) {
    return (
      <Navigate
        to={{ pathname: issuerDashboardPaths.static.securities, search: params.toString() }}
      />
    );
  }

  return (
    <PageLayout
      title="Buy backs"
      headerAction={<HeaderAddBuyBack isDeleted={isDeleted} setIsDeleted={setIsDeleted} />}
      contentClassName="px-0 py-2"
    >
      <BuyBacksBody isDeleted={isDeleted} />
    </PageLayout>
  );
};

interface HeaderAddBuyBackProps {
  isDeleted?: boolean;
  setIsDeleted?: Dispatch<SetStateAction<boolean | undefined>>;
}

export const HeaderAddBuyBack = ({ isDeleted, setIsDeleted }: HeaderAddBuyBackProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const { allowsFractionalShares } = useOutletContext<IssuerDashboardWrapperOutletContext>();
  const handleOpenAddBuyBackModal = useCallback(() => {
    openModal(IssuerAddBuyBackModal, {
      defaultAllowsFractionalShares: allowsFractionalShares,
    });
  }, [allowsFractionalShares, openModal]);
  return (
    <div className="flex items-center gap-4">
      {setIsDeleted && (
        <ArchiveSwitch
          active={!!isDeleted}
          onChange={active => setIsDeleted(active || undefined)}
        />
      )}
      <Button className="w-[156px] px-0" onClick={handleOpenAddBuyBackModal}>
        Add new buy back
      </Button>
    </div>
  );
};

interface BuyBacksBodyProps {
  rightBlock?: ReactNode;
  hideChooseRegisterClass?: boolean;
  register_class_id?: number;
  isDeleted?: boolean;
}

export const BuyBacksBody = ({
  rightBlock,
  hideChooseRegisterClass = false,
  register_class_id,
  isDeleted = false,
}: BuyBacksBodyProps) => {
  const { isMobile } = useIsMobile();
  const { openModal } = useContext(ModalProviderContext);
  const { registerId } = useOutletContext<IssuerDashboardWrapperOutletContext>();
  const [archivingId, setArchivingId] = useState<number | null>(null);
  const [filters, setFilters] = useSaveState<transactionsFilters>(
    {
      register_class_id,
    },
    `filters-buy-backs-${registerId}`,
  );

  const register = useGetRegister(registerId);
  const [params] = useSearchParams();

  const {
    data: dataList,
    isLoading,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetching,
  } = useBuyBackEventsQuery(
    {
      page: 1,
      page_size: 30,
      ...pickBy<GetBuyBackEventListV1SrBuyBackEventsGetParams>(
        {
          register_class_id: register_class_id ?? filters.register_class_id,
          event_date_to: filters.requested_at_date_to,
          event_date_from: filters.requested_at_date_from,
          is_deleted: isDeleted,
        },
        Boolean,
      ),
    },
    false,
    +registerId,
  );

  const { mutateAsync: mutateAsyncBuyBackEventArchive, isLoading: isLoadingBuyBackEventArchive } =
    useBuyBackEventArchiveByIdMutation();

  const headItems = useMemo<TableHeadColumn<TypeHead>[]>(() => {
    let items: TableHeadColumn<TypeHead>[] = [
      { title: 'ID', className: '' },
      {
        title: 'Class',
        className: '',
      },
      {
        title: 'Accepted offers',
        className: 'min-w-[200px]',
      },
      {
        title: 'Term',
        className: 'min-w-[200px]',
      },
      {
        title: 'Holders',
        className: 'min-w-[140px]',
      },
      {
        title: 'Total',
        className: '',
      },
      {
        title: '',
      },
    ];

    if (hideChooseRegisterClass) {
      items = items.filter(item => item.title !== 'Class');
    }

    if (isDeleted) {
      items = items.slice(0, -1);
    }

    return items;
  }, [hideChooseRegisterClass, isDeleted]);

  const handleOpenOperationInfoModal = useCallback(
    (item: BuyBackEventCalculateResponse) => {
      openModal(IssuerBuyBackOperationInfoModal, {
        item,
        isDeleted,
      });
    },
    [isDeleted, openModal],
  );

  const handleArchive = useCallback(
    (id: number) => {
      openModal(ConfirmModal, {
        title: 'Archive transaction?',
        onConfirm: async () => {
          try {
            setArchivingId(id);
            await mutateAsyncBuyBackEventArchive(id);
          } finally {
            setArchivingId(null);
          }
        },
      });
    },
    [mutateAsyncBuyBackEventArchive, openModal],
  );

  const bodyItems = useMemo<TableBodyColumn[]>(() => {
    if (!dataList) {
      return [];
    }

    let result: TableBodyColumn[] = [];

    dataList.pages.forEach(page => {
      result = result.concat(
        page.data?.map(data => {
          let body: BodyCell[] = [
            {
              cell: (
                <span className="text-sm leading-[22px] text-[#0369A1] font-[500]">#{data.id}</span>
              ),
            },
            {
              cell: <span>{data.register_class?.class_name ?? '-'}</span>,
            },
            {
              cell: (
                <div className="flex gap-2">
                  <Progress
                    total={data.total_buy_back_offer}
                    current={data.current_buy_back_offer ?? 0}
                    showInfo={false}
                  />
                  <div className="text-[12px] text-nowrap">
                    {data.current_buy_back_offer ?? 0} / {data.total_buy_back_offer}
                  </div>
                </div>
              ),
            },
            {
              cell: (
                <div className="">{`${dateToPrint(
                  data.open_event_date,
                  DateVariant.Dashboard,
                  false,
                )} - ${dateToPrint(
                  dayjs(data.close_event_date || '')
                    .add(1, 'minute')
                    .toISOString(),
                  DateVariant.Dashboard,
                  false,
                )}`}</div>
              ),
            },
            {
              cell: <span>{formatNumberWithCommas(data.total_holder_quantity)}</span>,
            },
            {
              cell: (
                <span>{`$${formatNumberWithCommas(
                  Math.round(data.total_buy_back_offer * 100) / 100,
                )}`}</span>
              ),
            },
            {
              cell: (
                <span>
                  {isLoadingBuyBackEventArchive && archivingId === data.id ? (
                    <Spinner size="sm" className="m-0" />
                  ) : (
                    <TooltipNew text="Archive" className="w-5 h-5 min-w-5 min-h-5">
                      <img
                        src={TrashGrayIcon}
                        alt="Archive"
                        className="w-5 h-5 min-w-5 min-h-5 cursor-pointer hover:opacity-75"
                        onClick={e => {
                          e.stopPropagation();
                          handleArchive(data.id);
                        }}
                      />
                    </TooltipNew>
                  )}
                </span>
              ),
            },
          ];

          if (hideChooseRegisterClass) {
            body = body.filter((_, i) => i !== 1); //remove class
          }

          if (isDeleted) {
            body = body.slice(0, -1); //remove archive
          }

          return {
            cells: body,
            onClick: () => handleOpenOperationInfoModal(data),
          };
        }),
      );
    });

    return result;
  }, [
    archivingId,
    dataList,
    handleArchive,
    handleOpenOperationInfoModal,
    hideChooseRegisterClass,
    isDeleted,
    isLoadingBuyBackEventArchive,
  ]);

  if (!!register?.type && register?.type !== RegisterType.Company) {
    return (
      <Navigate
        to={{ pathname: issuerDashboardPaths.static.securities, search: params.toString() }}
      />
    );
  }

  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex items-start justify-between gap-4 w-full px-3 sm:px-6">
        <FiltersPanel
          setFilters={setFilters}
          filters={filters}
          registerId={registerId}
          isFetching={isFetching}
          hideChooseRegisterClass={hideChooseRegisterClass}
          forBuyBack={true}
        />
        {rightBlock}
      </div>
      <Table
        className="my-0"
        classNameHead="border-t-0"
        withRounded={isMobile}
        withBorder={true}
        withBorderX={false}
        headItems={headItems}
        items={bodyItems}
        isLoading={isLoading}
        isError={isError}
        hasNextPageInfinite={hasNextPage}
        fetchNextPageInfinite={fetchNextPage}
        blockIdInfinite="scrollable"
      />
    </div>
  );
};

export default withPrivateAccess(IssuerBuyBack, [Role.SR_ISSUER, Role.SR_ISSUER_ADMIN]);
